import React, { useEffect, useMemo, useState } from "react";
import { ScanLine } from "lucide-react";
import { toast } from "sonner";

import ScanPage from "../employee/ScanPage";
import { useAuth } from "../../context/AuthContext";
import api, { formatApiErrorDetail } from "../../lib/api";
import { deptLabel } from "../../lib/labels";
import {
  activeTenantStores,
  initialHqStoreId,
  managerStoreIsLocked,
} from "../../lib/hqScanContext";
import Card from "../../components/system/Card";

export default function HQScanPage() {
  const { user } = useAuth();
  const [stores, setStores] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [storeId, setStoreId] = useState("");
  const [departmentId, setDepartmentId] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    Promise.all([api.get("/stores"), api.get("/departments")])
      .then(([storeResponse, departmentResponse]) => {
        if (cancelled) return;
        const nextStores = activeTenantStores(storeResponse.data);
        setStores(nextStores);
        setDepartments(Array.isArray(departmentResponse.data) ? departmentResponse.data : []);
        setStoreId(initialHqStoreId(user, nextStores));
      })
      .catch((error) => {
        if (!cancelled) toast.error(formatApiErrorDetail(error));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [user]);

  const managerLocked = managerStoreIsLocked(user, storeId);
  const selectedStore = useMemo(
    () => stores.find((store) => store.id === storeId) || null,
    [stores, storeId],
  );

  return (
    <div data-testid="hq-scan-page">
      <Card variant="raised" className="p-4 mb-4" data-testid="hq-scan-context">
        <div className="flex items-center gap-2 mb-3">
          <ScanLine size={18} className="text-brand" />
          <div>
            <div className="font-display font-black text-lg">Floor Mode</div>
            <div className="text-xs text-ink/60">
              Choose where this scan belongs before recording stock or submitting a request.
            </div>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <label className="block">
            <span className="data-sku block mb-1">Store · required</span>
            <select
              value={storeId}
              onChange={(event) => setStoreId(event.target.value)}
              disabled={managerLocked || loading}
              className="w-full h-11 px-3 rounded-button bg-paper-2 border border-border/60 font-mono text-xs disabled:opacity-70"
              data-testid="hq-scan-store"
            >
              <option value="">— Select store —</option>
              {stores.map((store) => (
                <option key={store.id} value={store.id}>{store.name}</option>
              ))}
            </select>
            {managerLocked && (
              <span className="block mt-1 text-[11px] text-ink/60" data-testid="hq-scan-manager-store-locked">
                Assigned store is locked for Store Managers.
              </span>
            )}
          </label>
          <label className="block">
            <span className="data-sku block mb-1">Department</span>
            <select
              value={departmentId}
              onChange={(event) => setDepartmentId(event.target.value)}
              disabled={loading}
              className="w-full h-11 px-3 rounded-button bg-paper-2 border border-border/60 font-mono text-xs"
              data-testid="hq-scan-department"
            >
              <option value="">— Derive from product —</option>
              {departments.map((department) => (
                <option key={department.id} value={department.id}>{deptLabel(department.name)}</option>
              ))}
            </select>
            <span className="block mt-1 text-[11px] text-ink/60">
              Required when requesting an unknown product; recognized products use their catalog department.
            </span>
          </label>
        </div>
      </Card>

      {!loading && !selectedStore ? (
        <Card className="p-8 text-center" data-testid="hq-scan-context-required">
          <div className="font-display font-black text-xl mb-2">Select a store to start scanning</div>
          <div className="text-sm text-ink/60">
            Multi-store admins must choose the destination store explicitly.
          </div>
        </Card>
      ) : !loading ? (
        <ScanPage
          key={`${storeId}:${departmentId}`}
          operationalContext={{
            storeId,
            departmentId,
            storeName: selectedStore?.name,
            requireDepartmentForRequest: true,
          }}
        />
      ) : null}
    </div>
  );
}
